import { useState, useEffect, useCallback } from 'react';
import type { DomainConfig, SenderAccount } from '../layout/AccountSwitcher';
import { getRegionDomain, getCommonRegion, getRegionLabel } from '../../utils/regionMap';
import { getCountryLanguage, isEnglishCountry, getLanguagesForCountries } from '../../utils/countryLanguage';
import { authFetch } from '../../utils/authFetch';

interface Props {
    accounts: DomainConfig[];
    defaultSender: SenderAccount | null;
    onClose: () => void;
}

interface Lead {
    id: string;
    campaignId: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    country: string;
    status: string;
}

interface Campaign {
    id: string;
    name: string;
}

const PLACEHOLDERS = ['{{name}}', '{{company}}', '{{title}}', '{{country}}', '{{senderName}}', '{{senderEmail}}'];

function renderTemplate(template: string, vars: Record<string, string>): string {
    let result = template;
    for (const [key, val] of Object.entries(vars)) {
        result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val);
    }
    return result;
}

const REGION_ICONS: Record<string, string> = {
    US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', AR: '🇦🇷', UY: '🇺🇾', BR: '🇧🇷',
    CO: '🇨🇴', CL: '🇨🇱', ES: '🇪🇸', FR: '🇫🇷', DE: '🇩🇪', GB: '🇬🇧',
    AE: '🇦🇪', SG: '🇸🇬', AU: '🇦🇺', JP: '🇯🇵', KR: '🇰🇷', CN: '🇨🇳',
};

export function ComposeModal({ accounts, defaultSender, onClose }: Props) {
    const allSenders = accounts.flatMap(d => d.senders);
    const [fromEmail, setFromEmail] = useState(defaultSender?.email || allSenders[0]?.email || '');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Outlook pool senders
    const [outlookSenders, setOutlookSenders] = useState<{ email: string; displayName: string; warmupScore: number; provider: string }[]>([]);
    const [outlookSearch, setOutlookSearch] = useState('');

    // Test email mode
    const [testMode, setTestMode] = useState(false);
    const [manualToEmail, setManualToEmail] = useState('');
    const [testName, setTestName] = useState('John Smith');
    const [testCompany, setTestCompany] = useState('Baker McKenzie');
    const [testTitle, setTestTitle] = useState('Managing Partner');
    const [testCountry, setTestCountry] = useState('US');

    // All contacts data
    const [allContacts, setAllContacts] = useState<Lead[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Filters
    const [countryFilter, setCountryFilter] = useState('all');
    const [campaignFilter, setCampaignFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Auto-send
    const [sendProgress, setSendProgress] = useState(0);
    const [sendTotal, setSendTotal] = useState(0);
    const [sendResults, setSendResults] = useState<{ email: string; ok: boolean; lang?: string; via?: string }[]>([]);
    const [previewIdx, setPreviewIdx] = useState(0);

    // Multi-sender rotation mode: 'single' = one fixed sender, 'rotate' = round-robin selected senders
    const [senderMode, setSenderMode] = useState<'single' | 'rotate'>('rotate');
    // Which senders are selected for rotation (default: all)
    const [selectedSenderEmails, setSelectedSenderEmails] = useState<Set<string>>(new Set(allSenders.map(s => s.email)));

    // ── Active step: 'select' → 'compose' → 'preview' → 'sending' → 'done'
    const [step, setStep] = useState<'select' | 'compose' | 'preview' | 'sending' | 'done'>(
        allContacts.length === 0 && !loading ? 'select' : 'select'
    );

    const sender = allSenders.find(s => s.email === fromEmail);

    // ── Load all contacts on mount ──
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                // Load campaigns + outlook senders in parallel
                const [campaignsRes, outlookRes] = await Promise.all([
                    authFetch('/campaigns'),
                    authFetch('/dispatch/outlook-senders').catch(() => null),
                ]);
                const data = await campaignsRes.json();
                const campaignList: Campaign[] = data.campaigns || [];
                setCampaigns(campaignList);
                const all: Lead[] = [];
                for (const c of campaignList) {
                    try {
                        const lr = await authFetch(`/campaigns/${c.id}`);
                        const ld = await lr.json();
                        all.push(...(ld.leads || []));
                    } catch { }
                }
                // Dedup by email
                const emailMap = new Map<string, Lead>();
                for (const lead of all) emailMap.set(lead.email.toLowerCase(), lead);
                setAllContacts(Array.from(emailMap.values()));

                // Load outlook pool senders
                if (outlookRes) {
                    const olData = await outlookRes.json();
                    if (olData.success && olData.senders) {
                        setOutlookSenders(olData.senders);
                    }
                }
            } catch { }
            setLoading(false);
        })();
    }, []);

    // ── Filtered contacts ──
    const filtered = allContacts.filter(l => {
        if (countryFilter !== 'all' && l.country !== countryFilter) return false;
        if (campaignFilter !== 'all' && l.campaignId !== campaignFilter) return false;
        if (statusFilter !== 'all' && l.status !== statusFilter) return false;
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            return l.name.toLowerCase().includes(q) ||
                l.companyName.toLowerCase().includes(q) ||
                l.email.toLowerCase().includes(q) ||
                l.title.toLowerCase().includes(q);
        }
        return true;
    });

    const selectedContacts = allContacts.filter(l => selectedIds.has(l.id));
    const countries = Array.from(new Set(allContacts.map(l => l.country))).sort();
    const statuses = Array.from(new Set(allContacts.map(l => l.status))).sort();

    // Region lock
    const commonRegion = getCommonRegion(selectedContacts.map(l => l.country));
    const isMixedRegion = selectedContacts.length > 1 && !commonRegion;

    // Filter senders by region
    const allowedSenders = commonRegion
        ? allSenders.filter(s => s.email.endsWith(`@${commonRegion}`))
        : allSenders;

    // Language info
    const targetLangs = getLanguagesForCountries(selectedContacts.map(l => l.country));
    const hasNonEnglish = targetLangs.some(l => l.code !== 'en');

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === filtered.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filtered.map(l => l.id)));
        }
    };

    const selectByCountry = (country: string) => {
        const ids = allContacts.filter(l => l.country === country).map(l => l.id);
        setSelectedIds(new Set(ids));
    };

    // ── Auto-send ──
    const autoSend = async () => {
        // Test mode: send to manual email
        if (testMode) {
            // In rotate mode, use first selected sender; in single mode, use fromEmail
            const testSenderEmail = senderMode === 'rotate'
                ? Array.from(selectedSenderEmails)[0] || fromEmail
                : fromEmail;
            const testSender = allSenders.find(s => s.email === testSenderEmail)
                || outlookSenders.find(s => s.email === testSenderEmail);
            if (!manualToEmail || !subject || !body || !testSenderEmail) return;
            setStep('sending');
            setSendTotal(1);
            setSendProgress(0);
            setSendResults([]);
            const vars: Record<string, string> = {
                name: testName || '[Test Name]',
                company: testCompany || '[Test Company]',
                title: testTitle || '[Test Title]',
                country: testCountry || '[Test Country]',
                senderName: testSender?.displayName || testSenderEmail.split('@')[0],
                senderEmail: testSenderEmail,
            };
            try {
                await authFetch('/emails/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        toEmail: manualToEmail,
                        fromEmail: testSenderEmail,
                        subject: renderTemplate(subject, vars),
                        bodyText: renderTemplate(body, vars),
                    }),
                });
                setSendResults([{ email: manualToEmail, ok: true }]);
            } catch {
                setSendResults([{ email: manualToEmail, ok: false }]);
            }
            setSendProgress(1);
            setStep('done');
            return;
        }

        if (selectedContacts.length === 0 || !subject || !body) return;
        setStep('sending');
        setSendProgress(0);
        setSendResults([]);

        // Determine which senders to use (merge Spacemail + Outlook Pool)
        const availablePool = commonRegion ? allowedSenders : allSenders;
        const spacemailPool = senderMode === 'rotate'
            ? availablePool.filter(s => selectedSenderEmails.has(s.email))
            : [sender!].filter(Boolean);
        // Add selected Outlook Pool accounts as senders
        const outlookPool = senderMode === 'rotate'
            ? outlookSenders
                .filter(s => selectedSenderEmails.has(s.email))
                .map(s => ({ email: s.email, displayName: s.displayName, sentToday: 0, dailySendLimit: 5 }))
            : [];
        const sendersPool = [...spacemailPool, ...outlookPool];

        if (sendersPool.length === 0) return;

        // ── Step 1: Verify emails before sending ─────────────────────
        let verifiedContacts = selectedContacts;
        try {
            const verifyRes = await authFetch('/emails/verify', {
                method: 'POST',
                body: JSON.stringify({ emails: selectedContacts.map(c => c.email) }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success && verifyData.invalid?.length > 0) {
                const invalidSet = new Set(verifyData.invalid.map((r: any) => r.email));
                // Log invalid ones as skipped
                for (const inv of verifyData.invalid) {
                    setSendResults(prev => [...prev, {
                        email: inv.email,
                        ok: false,
                        lang: undefined,
                        via: `⛔ ${inv.reason || 'Invalid email'}`,
                    }]);
                }
                verifiedContacts = selectedContacts.filter(c => !invalidSet.has(c.email.toLowerCase()));
            }
        } catch {
            // Verification failed — proceed with all contacts
            console.warn('[Compose] Email verification unavailable, sending to all');
        }

        setSendTotal(verifiedContacts.length + (selectedContacts.length - verifiedContacts.length));

        // ── Step 2: Send to verified contacts ────────────────────────
        let sentIdx = selectedContacts.length - verifiedContacts.length; // account for skipped

        for (let i = 0; i < verifiedContacts.length; i++) {
            const contact = verifiedContacts[i];
            const lang = getCountryLanguage(contact.country);
            // Round-robin sender selection
            const currentSender = sendersPool[i % sendersPool.length];
            const vars: Record<string, string> = {
                name: contact.name,
                company: contact.companyName,
                title: contact.title,
                country: contact.country,
                senderName: currentSender.displayName || currentSender.email.split('@')[0],
                senderEmail: currentSender.email || '',
            };
            try {
                await authFetch('/emails/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        leadId: contact.id,
                        toEmail: contact.email,
                        fromEmail: currentSender.email,
                        subject: renderTemplate(subject, vars),
                        bodyText: renderTemplate(body, vars),
                        targetLanguage: isEnglishCountry(contact.country) ? undefined : lang.code,
                    }),
                });
                setSendResults(prev => [...prev, { email: contact.email, ok: true, lang: lang.nativeName, via: currentSender.email }]);
            } catch {
                setSendResults(prev => [...prev, { email: contact.email, ok: false, via: currentSender.email }]);
            }
            sentIdx++;
            setSendProgress(sentIdx);
            // Small delay between sends
            if (i < verifiedContacts.length - 1) {
                await new Promise(r => setTimeout(r, 300));
            }
        }
        setStep('done');
    };

    // In rotate mode, show the sender that would be assigned to this preview contact
    const previewSenderPool = senderMode === 'rotate'
        ? allSenders.filter(s => selectedSenderEmails.has(s.email))
        : [sender].filter(Boolean);
    const previewSender = previewSenderPool.length > 0
        ? previewSenderPool[previewIdx % previewSenderPool.length]
        : sender;
    const previewVars = selectedContacts[previewIdx] ? {
        name: selectedContacts[previewIdx].name,
        company: selectedContacts[previewIdx].companyName,
        title: selectedContacts[previewIdx].title,
        country: selectedContacts[previewIdx].country,
        senderName: previewSender?.displayName || '',
        senderEmail: previewSender?.email || '',
    } : {
        name: '[Name]', company: '[Company]', title: '[Title]',
        country: '[Country]', senderName: previewSender?.displayName || '', senderEmail: previewSender?.email || '',
    };

    return (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
            <div className="modal-panel" style={{ width: step === 'select' ? 900 : 720, maxHeight: '85vh', overflow: 'auto' }}>

                {/* ── Header ── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                            {step === 'select' && '📋 Select Recipients'}
                            {step === 'compose' && '✉️ Compose Email'}
                            {step === 'preview' && `👁️ Preview (${previewIdx + 1}/${selectedContacts.length})`}
                            {step === 'sending' && `📤 Sending... ${sendProgress}/${sendTotal}`}
                            {step === 'done' && '✅ Sending Complete'}
                        </h2>
                        {step === 'select' && (
                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                                Select contacts to send batch emails. {selectedIds.size > 0 && `${selectedIds.size} selected`}
                            </p>
                        )}
                    </div>
                    {step !== 'sending' && (
                        <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
                    )}
                </div>

                {/* ═══════════════════════════════ STEP 1: SELECT ═══════════════════════════════ */}
                {step === 'select' && (
                    <>
                        {/* Quick filters */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input className="input" style={{ flex: 1, minWidth: 180 }}
                                placeholder="🔍 Search contacts..."
                                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                            <select className="input" style={{ width: 'auto' }} value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}>
                                <option value="all">🌍 All countries</option>
                                {countries.map(c => <option key={c} value={c}>{REGION_ICONS[c] || '🌍'} {c}</option>)}
                            </select>

                            <select className="input" style={{ width: 'auto' }} value={campaignFilter}
                                onChange={(e) => setCampaignFilter(e.target.value)}>
                                <option value="all">📂 All campaigns</option>
                                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>

                            <select className="input" style={{ width: 'auto' }} value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All statuses</option>
                                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        {/* Quick select buttons */}
                        {countries.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>Quick select:</span>
                                {countries.map(c => (
                                    <button key={c} className="btn btn-outline btn-sm"
                                        style={{ fontSize: 10, padding: '2px 8px' }}
                                        onClick={() => selectByCountry(c)}>
                                        {REGION_ICONS[c] || '🌍'} All {c}
                                    </button>
                                ))}
                                <button className="btn btn-outline btn-sm"
                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                    onClick={toggleAll}>
                                    {selectedIds.size === filtered.length ? '☐ Deselect all' : '☑️ Select all visible'}
                                </button>
                            </div>
                        )}

                        {/* Contact list */}
                        <div className="card" style={{ padding: 0, maxHeight: 360, overflow: 'auto' }}>
                            {loading ? (
                                <p style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading contacts...</p>
                            ) : filtered.length === 0 ? (
                                <p style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>No contacts found</p>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 32 }}>
                                                <input type="checkbox" className="checkbox"
                                                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                                                    onChange={toggleAll} />
                                            </th>
                                            <th>Name</th>
                                            <th>Title</th>
                                            <th>Company</th>
                                            <th>Email</th>
                                            <th>Country</th>
                                            <th>Lang</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(l => (
                                            <tr key={l.id} style={{ cursor: 'pointer' }}
                                                onClick={() => toggleSelect(l.id)}>
                                                <td onClick={e => e.stopPropagation()}>
                                                    <input type="checkbox" className="checkbox"
                                                        checked={selectedIds.has(l.id)}
                                                        onChange={() => toggleSelect(l.id)} />
                                                </td>
                                                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{l.name}</td>
                                                <td style={{ fontSize: 12 }}>{l.title}</td>
                                                <td style={{ fontSize: 12 }}>{l.companyName}</td>
                                                <td style={{ fontSize: 12, color: 'var(--accent-light)' }}>{l.email}</td>
                                                <td style={{ fontSize: 12 }}>{REGION_ICONS[l.country] || '🌍'} {l.country}</td>
                                                <td>{(() => {
                                                    const lang = getCountryLanguage(l.country); return (
                                                        <span style={{
                                                            fontSize: 10, padding: '2px 5px', borderRadius: 4,
                                                            background: isEnglishCountry(l.country) ? 'transparent' : 'rgba(168,85,247,0.15)',
                                                            color: isEnglishCountry(l.country) ? 'var(--text-muted)' : '#c084fc',
                                                        }}>
                                                            {isEnglishCountry(l.country) ? 'EN' : lang.nativeName}
                                                        </span>
                                                    );
                                                })()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Region lock warning */}
                        {isMixedRegion && selectedIds.size > 0 && (
                            <div style={{
                                marginTop: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
                                border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8,
                                fontSize: 12, color: '#f59e0b',
                            }}>
                                ⚠️ Selected contacts span multiple regions — sender will be limited to accounts matching the common region.
                            </div>
                        )}

                        {/* Bottom: Next step */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                {filtered.length} contacts · {selectedIds.size} selected
                                {commonRegion && ` · 🔒 ${getRegionLabel(commonRegion)}`}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-outline" onClick={() => {
                                    setTestMode(true);
                                    setStep('compose');
                                }}>
                                    🧪 Send Test Email
                                </button>
                                <button className="btn btn-primary" onClick={() => {
                                    setTestMode(false);
                                    setStep('compose');
                                }}
                                    disabled={selectedIds.size === 0}>
                                    Next: Compose Email →
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 2: COMPOSE ═══════════════════════════════ */}
                {step === 'compose' && (
                    <>
                        {/* Summary bar */}
                        {testMode ? (
                            <div style={{
                                padding: '10px 14px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.25)',
                                borderRadius: 8, marginBottom: 16,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#c084fc' }}>🧪 Test Email Mode</span>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>— Send a single test email to any address</span>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <label className="section-title">To (recipient email)</label>
                                    <input className="input" value={manualToEmail}
                                        onChange={(e) => setManualToEmail(e.target.value)}
                                        placeholder="e.g. test@example.com"
                                        type="email" />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <div>
                                        <label className="section-title">Recipient Name</label>
                                        <input className="input" value={testName}
                                            onChange={(e) => setTestName(e.target.value)}
                                            placeholder="e.g. John Smith" />
                                    </div>
                                    <div>
                                        <label className="section-title">Company</label>
                                        <input className="input" value={testCompany}
                                            onChange={(e) => setTestCompany(e.target.value)}
                                            placeholder="e.g. Baker McKenzie" />
                                    </div>
                                    <div>
                                        <label className="section-title">Title</label>
                                        <input className="input" value={testTitle}
                                            onChange={(e) => setTestTitle(e.target.value)}
                                            placeholder="e.g. Managing Partner" />
                                    </div>
                                    <div>
                                        <label className="section-title">Country</label>
                                        <input className="input" value={testCountry}
                                            onChange={(e) => setTestCountry(e.target.value)}
                                            placeholder="e.g. US" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8,
                                marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                                <div>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>📤 {selectedContacts.length} recipients</span>
                                    {commonRegion && (
                                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent-light)' }}>
                                            🔒 {getRegionLabel(commonRegion)}
                                        </span>
                                    )}
                                </div>
                                {hasNonEnglish && (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                        <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>🌐 Auto-translate:</span>
                                        {targetLangs.filter(l => l.code !== 'en').map(l => (
                                            <span key={l.code} style={{
                                                fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                                background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                            }}>
                                                {l.flag} {l.nativeName}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Sender Mode */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">From</label>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <button
                                    className={`btn btn-sm ${senderMode === 'rotate' ? 'btn-primary' : 'btn-outline'}`}
                                    onClick={() => setSenderMode('rotate')}
                                    style={{ fontSize: 11 }}
                                >
                                    🔄 Multi-account ({selectedSenderEmails.size} selected)
                                </button>
                                <button
                                    className={`btn btn-sm ${senderMode === 'single' ? 'btn-primary' : 'btn-outline'}`}
                                    onClick={() => setSenderMode('single')}
                                    style={{ fontSize: 11 }}
                                >
                                    👤 Single account
                                </button>
                            </div>
                            {senderMode === 'single' ? (
                                <select className="input" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}>
                                    <optgroup label="✉️ Spacemail (lawhelper.*)">
                                        {(commonRegion ? allowedSenders : allSenders).map(s => (
                                            <option key={s.email} value={s.email}>{s.displayName} &lt;{s.email}&gt;</option>
                                        ))}
                                    </optgroup>
                                    {outlookSenders.length > 0 && (
                                        <optgroup label={`📧 Outlook Pool (${outlookSenders.length})`}>
                                            {outlookSenders.slice(0, 50).map(s => (
                                                <option key={s.email} value={s.email}>{s.displayName} &lt;{s.email}&gt;</option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                            ) : (
                                <div style={{
                                    padding: '10px 12px', background: 'rgba(34,197,94,0.06)',
                                    border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8,
                                }}>
                                    <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginBottom: 8 }}>
                                        ✅ Select accounts to rotate (round-robin):
                                    </div>

                                    {/* Spacemail group */}
                                    <div style={{ marginBottom: 8 }}>
                                        <div style={{
                                            fontSize: 10, fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase',
                                            letterSpacing: 0.5, marginBottom: 4, padding: '0 8px',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                        }}>
                                            <span>✉️ Spacemail ({(commonRegion ? allowedSenders : allSenders).length})</span>
                                            {(commonRegion ? allowedSenders : allSenders).some(s => selectedSenderEmails.has(s.email)) && (
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedSenderEmails(prev => {
                                                            const next = new Set(prev);
                                                            allSenders.forEach(s => next.delete(s.email));
                                                            // Ensure at least 1 sender remains somewhere
                                                            if (next.size === 0 && outlookSenders.length > 0) {
                                                                // Auto-select top Outlook account
                                                                const top = [...outlookSenders].sort((a, b) => b.warmupScore - a.warmupScore)[0];
                                                                if (top) next.add(top.email);
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                    style={{
                                                        fontSize: 9, padding: '1px 6px', borderRadius: 3,
                                                        border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)',
                                                        color: '#ef4444', cursor: 'pointer',
                                                    }}
                                                >✕ Deselect all</button>
                                            )}
                                        </div>
                                        {(commonRegion ? allowedSenders : allSenders).map((s, i) => {
                                            const checked = selectedSenderEmails.has(s.email);
                                            return (
                                                <label key={s.email} style={{
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                    padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                                                    marginBottom: 2,
                                                    background: checked ? 'rgba(34,197,94,0.1)' : 'transparent',
                                                    transition: 'background 0.15s',
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox"
                                                        checked={checked}
                                                        onChange={() => {
                                                            setSelectedSenderEmails(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(s.email)) {
                                                                    // Allow deselect if other senders remain
                                                                    if (next.size > 1) next.delete(s.email);
                                                                } else {
                                                                    next.add(s.email);
                                                                }
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: checked ? 600 : 400 }}>
                                                        {s.displayName}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                                        &lt;{s.email}&gt;
                                                    </span>
                                                    <span style={{
                                                        marginLeft: 'auto', fontSize: 10, padding: '1px 6px',
                                                        borderRadius: 4, background: 'var(--bg-elevated)',
                                                        color: 'var(--text-dim)',
                                                    }}>
                                                        {s.sentToday}/{s.dailySendLimit}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    {/* Outlook Pool group */}
                                    {outlookSenders.length > 0 && (() => {
                                        const sorted = [...outlookSenders].sort((a, b) => b.warmupScore - a.warmupScore);
                                        const filtered = outlookSearch
                                            ? sorted.filter(s => s.email.toLowerCase().includes(outlookSearch.toLowerCase()) || s.displayName.toLowerCase().includes(outlookSearch.toLowerCase()))
                                            : sorted;
                                        const olSelectedCount = outlookSenders.filter(s => selectedSenderEmails.has(s.email)).length;
                                        return (
                                        <div>
                                            <div style={{
                                                fontSize: 10, fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase',
                                                letterSpacing: 0.5, marginBottom: 6, padding: '0 8px',
                                                borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8,
                                                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                                            }}>
                                                <span>📧 Outlook Pool ({outlookSenders.length})</span>
                                                {olSelectedCount > 0 && (
                                                    <span style={{ color: '#22c55e', fontSize: 9, fontWeight: 800 }}>✓ {olSelectedCount} selected</span>
                                                )}
                                            </div>
                                            {/* Batch action buttons */}
                                            <div style={{ display: 'flex', gap: 4, padding: '0 8px', marginBottom: 6, flexWrap: 'wrap' }}>
                                                {[10, 20, 50].map(n => (
                                                    <button key={n} onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedSenderEmails(prev => {
                                                            const next = new Set(prev);
                                                            sorted.slice(0, n).forEach(s => next.add(s.email));
                                                            return next;
                                                        });
                                                    }} style={{
                                                        fontSize: 9, padding: '2px 8px', borderRadius: 3,
                                                        border: '1px solid rgba(56,189,248,0.25)', background: 'rgba(56,189,248,0.08)',
                                                        color: '#38bdf8', cursor: 'pointer',
                                                    }}>+ Top {n}</button>
                                                ))}
                                                <button onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedSenderEmails(prev => {
                                                        const next = new Set(prev);
                                                        sorted.forEach(s => next.add(s.email));
                                                        return next;
                                                    });
                                                }} style={{
                                                    fontSize: 9, padding: '2px 8px', borderRadius: 3,
                                                    border: '1px solid rgba(56,189,248,0.25)', background: 'rgba(56,189,248,0.08)',
                                                    color: '#38bdf8', cursor: 'pointer',
                                                }}>+ All {outlookSenders.length}</button>
                                                {olSelectedCount > 0 && (
                                                    <button onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedSenderEmails(prev => {
                                                            const next = new Set(prev);
                                                            outlookSenders.forEach(s => next.delete(s.email));
                                                            return next;
                                                        });
                                                    }} style={{
                                                        fontSize: 9, padding: '2px 8px', borderRadius: 3,
                                                        border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)',
                                                        color: '#ef4444', cursor: 'pointer',
                                                    }}>✕ Remove all</button>
                                                )}
                                            </div>
                                            {/* Search */}
                                            <div style={{ padding: '0 8px', marginBottom: 6 }}>
                                                <input
                                                    className="input"
                                                    style={{ fontSize: 11, padding: '4px 8px', height: 28 }}
                                                    placeholder={`🔍 Search ${outlookSenders.length} Outlook accounts...`}
                                                    value={outlookSearch}
                                                    onChange={(e) => setOutlookSearch(e.target.value)}
                                                />
                                            </div>
                                            {/* Scrollable list */}
                                            <div style={{ maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}>
                                                {filtered.map(s => {
                                                    const checked = selectedSenderEmails.has(s.email);
                                                    return (
                                                        <label key={s.email} style={{
                                                            display: 'flex', alignItems: 'center', gap: 8,
                                                            padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                                                            marginBottom: 1,
                                                            background: checked ? 'rgba(56,189,248,0.08)' : 'transparent',
                                                            transition: 'background 0.15s',
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox"
                                                                checked={checked}
                                                                onChange={() => {
                                                                    setSelectedSenderEmails(prev => {
                                                                        const next = new Set(prev);
                                                                        if (next.has(s.email)) {
                                                                            if (next.size > 1) next.delete(s.email);
                                                                        } else {
                                                                            next.add(s.email);
                                                                        }
                                                                        return next;
                                                                    });
                                                                }}
                                                            />
                                                            <span style={{ fontSize: 11, color: checked ? '#38bdf8' : 'var(--text-secondary)', fontWeight: checked ? 600 : 400, whiteSpace: 'nowrap' }}>
                                                                {s.displayName}
                                                            </span>
                                                            <span style={{ fontSize: 9, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {s.email}
                                                            </span>
                                                            <span style={{
                                                                marginLeft: 'auto', fontSize: 9, padding: '1px 5px',
                                                                borderRadius: 3, flexShrink: 0,
                                                                background: s.warmupScore >= 70 ? 'rgba(110,231,183,0.12)' : 'rgba(251,191,36,0.12)',
                                                                color: s.warmupScore >= 70 ? '#6ee7b7' : '#fbbf24',
                                                                fontWeight: 700,
                                                            }}>
                                                                ⭐ {s.warmupScore}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                                {filtered.length === 0 && (
                                                    <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px', textAlign: 'center' }}>
                                                        No accounts match "{outlookSearch}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })()}

                                    {selectedSenderEmails.size > 0 && selectedContacts.length > 0 && (
                                        <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--bg-elevated)', borderRadius: 6, fontSize: 10, color: 'var(--text-muted)' }}>
                                            📊 Each account sends ~{Math.ceil(selectedContacts.length / selectedSenderEmails.size)} emails
                                            {(() => {
                                                const smCount = allSenders.filter(s => selectedSenderEmails.has(s.email)).length;
                                                const olCount = outlookSenders.filter(s => selectedSenderEmails.has(s.email)).length;
                                                return (
                                                    <span> · {smCount > 0 && `${smCount} Spacemail`}{smCount > 0 && olCount > 0 && ' + '}{olCount > 0 && `${olCount} Outlook`}</span>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Subject */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">Subject</label>
                            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
                                placeholder="e.g. Quick opportunity for {{name}} at {{company}}" />
                        </div>

                        {/* Body */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">Body</label>
                            <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)}
                                rows={10} style={{ resize: 'vertical' }}
                                placeholder={`Hi {{name}},\n\nI came across {{company}} and wanted to reach out.\n\n[Your value proposition here]\n\nBest,\n{{senderName}}`} />
                        </div>

                        {/* Placeholder hints */}
                        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {PLACEHOLDERS.map(p => (
                                <span key={p} style={{
                                    padding: '2px 8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                    fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
                                }}
                                    onClick={() => setBody(prev => prev + p)}
                                >
                                    {p}
                                </span>
                            ))}
                        </div>

                        {hasNonEnglish && (
                            <div style={{
                                padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                                borderRadius: 8, marginBottom: 16, fontSize: 11, color: '#fbbf24',
                            }}>
                                💡 Write your email in English. It will be automatically translated to {targetLangs.filter(l => l.code !== 'en').map(l => l.nativeName).join(', ')} for recipients in those countries.
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                            <button className="btn btn-outline" onClick={() => { setTestMode(false); setStep('select'); }}>← Back</button>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {!testMode && (
                                    <button className="btn btn-outline" onClick={() => { setPreviewIdx(0); setStep('preview'); }}
                                        disabled={!subject || !body}>
                                        👁️ Preview
                                    </button>
                                )}
                                <button className="btn btn-primary btn-lg" onClick={autoSend}
                                    disabled={!fromEmail || !subject || !body || (testMode && !manualToEmail)}>
                                    {testMode
                                        ? '🧪 Send Test Email'
                                        : `📤 Send to ${selectedContacts.length} contacts`
                                    }
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 3: PREVIEW ═══════════════════════════════ */}
                {step === 'preview' && selectedContacts[previewIdx] && (
                    <>
                        <div style={{
                            padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8,
                            marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div style={{ fontSize: 13 }}>
                                <strong>{selectedContacts[previewIdx].name}</strong>
                                <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                                    {selectedContacts[previewIdx].title} @ {selectedContacts[previewIdx].companyName}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {!isEnglishCountry(selectedContacts[previewIdx].country) && (
                                    <span style={{
                                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                        background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                    }}>
                                        🌐 → {getCountryLanguage(selectedContacts[previewIdx].country).nativeName}
                                    </span>
                                )}
                                <button className="btn btn-outline btn-sm" disabled={previewIdx === 0}
                                    onClick={() => setPreviewIdx(i => i - 1)}>←</button>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                    {previewIdx + 1} / {selectedContacts.length}
                                </span>
                                <button className="btn btn-outline btn-sm" disabled={previewIdx === selectedContacts.length - 1}
                                    onClick={() => setPreviewIdx(i => i + 1)}>→</button>
                            </div>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <label className="section-title">Subject</label>
                            <div style={{
                                padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-default)', fontSize: 13, fontWeight: 600,
                            }}>
                                {renderTemplate(subject, previewVars)}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">Body</label>
                            <div style={{
                                padding: 16, background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-default)', fontSize: 13,
                                whiteSpace: 'pre-wrap', color: 'var(--text-primary)', minHeight: 160,
                            }}>
                                {renderTemplate(body, previewVars)}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                            <button className="btn btn-outline" onClick={() => setStep('compose')}>← Edit</button>
                            <button className="btn btn-primary btn-lg" onClick={autoSend}>
                                📤 Confirm & Send to {selectedContacts.length} contacts
                            </button>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 4: SENDING ═══════════════════════════════ */}
                {step === 'sending' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{
                            width: '100%', height: 8, background: 'var(--bg-elevated)',
                            borderRadius: 999, overflow: 'hidden', marginBottom: 16,
                        }}>
                            <div style={{
                                width: `${(sendProgress / sendTotal) * 100}%`,
                                height: '100%', borderRadius: 999,
                                background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                                transition: 'width 0.3s ease',
                            }} />
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                            Sending {sendProgress} of {sendTotal}...
                        </p>

                        {/* Live results */}
                        <div style={{ maxHeight: 200, overflowY: 'auto', textAlign: 'left' }}>
                            {sendResults.map((r, i) => (
                                <div key={i} style={{
                                    padding: '4px 12px', fontSize: 11, display: 'flex',
                                    justifyContent: 'space-between', alignItems: 'center',
                                    borderBottom: '1px solid var(--border-subtle)',
                                }}>
                                    <span>{r.ok ? '✅' : '❌'} {r.email}</span>
                                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        {r.via && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>via {r.via.split('@')[0]}</span>}
                                        {r.lang && <span style={{ color: 'var(--text-muted)' }}>→ {r.lang}</span>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════ STEP 5: DONE ═══════════════════════════════ */}
                {step === 'done' && (
                    <div style={{ padding: '16px 0' }}>
                        {/* Success header */}
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <div style={{ fontSize: 40, marginBottom: 8 }}>
                                {sendResults.every(r => r.ok) ? '✅' : '⚠️'}
                            </div>
                            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>
                                {sendResults.every(r => r.ok) ? 'Email Sent Successfully' : 'Sending Complete'}
                            </h3>
                            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                {sendResults.filter(r => r.ok).length} of {sendResults.length} delivered
                            </p>
                        </div>

                        {/* Sent email preview */}
                        <div style={{
                            border: '1px solid var(--border-default)',
                            borderRadius: 'var(--radius-lg)',
                            overflow: 'hidden',
                            marginBottom: 16,
                        }}>
                            {/* Email header bar */}
                            <div style={{
                                padding: '10px 14px',
                                background: 'var(--bg-elevated)',
                                borderBottom: '1px solid var(--border-subtle)',
                                fontSize: 12,
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ color: 'var(--text-muted)' }}>
                                        From: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{sender?.displayName || fromEmail}</span>
                                        {' '}&lt;{fromEmail}&gt;
                                    </span>
                                    <span style={{
                                        fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                        background: 'rgba(34,197,94,0.12)', color: '#22c55e', fontWeight: 700,
                                    }}>
                                        📤 Sent
                                    </span>
                                </div>
                                <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
                                    To: <span style={{ color: 'var(--text-primary)' }}>
                                        {testMode
                                            ? manualToEmail
                                            : sendResults.map(r => r.email).join(', ')
                                        }
                                    </span>
                                </div>
                                <div style={{ color: 'var(--text-muted)' }}>
                                    Subject: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                        {(() => {
                                            const vars = testMode
                                                ? { name: testName, company: testCompany, title: testTitle, country: testCountry, senderName: sender?.displayName || '', senderEmail: sender?.email || '' }
                                                : selectedContacts[0]
                                                    ? { name: selectedContacts[0].name, company: selectedContacts[0].companyName, title: selectedContacts[0].title, country: selectedContacts[0].country, senderName: sender?.displayName || '', senderEmail: sender?.email || '' }
                                                    : { name: '', company: '', title: '', country: '', senderName: '', senderEmail: '' };
                                            return renderTemplate(subject, vars);
                                        })()}
                                    </span>
                                </div>
                            </div>

                            {/* Email body preview */}
                            <div style={{
                                padding: '16px 18px',
                                fontSize: 13,
                                lineHeight: 1.7,
                                color: 'var(--text-primary)',
                                whiteSpace: 'pre-wrap',
                                maxHeight: 280,
                                overflowY: 'auto',
                                background: 'var(--bg-base)',
                            }}>
                                {(() => {
                                    const vars = testMode
                                        ? { name: testName, company: testCompany, title: testTitle, country: testCountry, senderName: sender?.displayName || '', senderEmail: sender?.email || '' }
                                        : selectedContacts[0]
                                            ? { name: selectedContacts[0].name, company: selectedContacts[0].companyName, title: selectedContacts[0].title, country: selectedContacts[0].country, senderName: sender?.displayName || '', senderEmail: sender?.email || '' }
                                            : { name: '', company: '', title: '', country: '', senderName: '', senderEmail: '' };
                                    return renderTemplate(body, vars);
                                })()}
                            </div>
                        </div>

                        {/* Recipient list (for batch sends) */}
                        {sendResults.length > 1 && (
                            <div style={{
                                border: '1px solid var(--border-default)',
                                borderRadius: 'var(--radius-lg)',
                                marginBottom: 16,
                                maxHeight: 160,
                                overflowY: 'auto',
                            }}>
                                <div style={{
                                    padding: '6px 12px',
                                    background: 'var(--bg-elevated)',
                                    borderBottom: '1px solid var(--border-subtle)',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: 'var(--text-muted)',
                                }}>
                                    Recipients ({sendResults.length})
                                </div>
                                {sendResults.map((r, i) => (
                                    <div key={i} style={{
                                        padding: '5px 12px', fontSize: 12, display: 'flex',
                                        justifyContent: 'space-between', alignItems: 'center',
                                        borderBottom: i < sendResults.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                                    }}>
                                        <span style={{ color: 'var(--text-primary)' }}>{r.ok ? '✅' : '❌'} {r.email}</span>
                                        {r.lang && <span style={{
                                            fontSize: 10, padding: '1px 5px', borderRadius: 4,
                                            background: 'rgba(168,85,247,0.12)', color: '#a855f7',
                                        }}>→ {r.lang}</span>}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Failed list */}
                        {sendResults.some(r => !r.ok) && (
                            <div style={{
                                padding: '8px 12px', background: 'rgba(239,68,68,0.08)',
                                border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8,
                                fontSize: 12, color: '#ef4444', marginBottom: 16,
                            }}>
                                ❌ Failed: {sendResults.filter(r => !r.ok).map(r => r.email).join(', ')}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            <button className="btn btn-outline" onClick={() => {
                                setStep('compose');
                                setSendResults([]);
                            }}>
                                ✉️ Send Another
                            </button>
                            <button className="btn btn-primary" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
